import { createBrowserEventId, normalizeBrowserEventId } from "./event-id"

const HISTORY_STATE_KEY = `${process.env.NEXT_PUBLIC_STORE_NAME}:meta:view-content`
const PIXEL_MARKER_PREFIX = `${process.env.NEXT_PUBLIC_STORE_NAME}:meta:view-content:pixel:`

type HistoryState = Record<string, unknown> | null | undefined

type ResolveViewContentLifecycleInput = {
  productId: string
  providedEventId?: string | null
  historyState?: HistoryState
  createEventId?: () => string
}

type StoredViewContentState = Record<string, string>

const readStoredEvents = (state: HistoryState): StoredViewContentState => {
  if (!state || typeof state !== "object") return {}
  const candidate = state[HISTORY_STATE_KEY]
  if (!candidate || typeof candidate !== "object" || Array.isArray(candidate)) {
    return {}
  }
  return Object.fromEntries(
    Object.entries(candidate).flatMap(([key, value]) => {
      const eventId = normalizeBrowserEventId(value)
      return key && eventId ? [[key, eventId]] : []
    })
  )
}

export const resolveViewContentLifecycle = ({
  productId,
  providedEventId,
  historyState,
  createEventId = createBrowserEventId,
}: ResolveViewContentLifecycleInput): {
  eventId: string
  nextHistoryState: Record<string, unknown>
} => {
  const normalizedProductId = String(productId || "").trim()
  const state =
    historyState && typeof historyState === "object" ? historyState : {}
  const storedEvents = readStoredEvents(state)
  const eventId =
    normalizeBrowserEventId(providedEventId) ||
    storedEvents[normalizedProductId] ||
    createEventId()

  return {
    eventId,
    nextHistoryState: {
      ...state,
      [HISTORY_STATE_KEY]: {
        ...storedEvents,
        [normalizedProductId]: eventId,
      },
    },
  }
}

export const claimViewContentPixelEvent = (
  eventId: string,
  storage?: Pick<Storage, "getItem" | "setItem"> | null
): boolean => {
  const normalized = normalizeBrowserEventId(eventId)
  if (!normalized) return false
  const target =
    storage ?? (typeof window !== "undefined" ? window.sessionStorage : null)
  if (!target) return true
  const key = `${PIXEL_MARKER_PREFIX}${normalized}`
  try {
    if (target.getItem(key)) return false
    target.setItem(key, "1")
    return true
  } catch {
    return true
  }
}
